import { useEffect, useState, useMemo } from 'react';
import { usePersonasStore } from '@/stores/personas';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  User,
  Loader2,
  AlertCircle,
  ChevronRight,
  ToggleLeft,
  ToggleRight,
  X,
  Search,
  Building2,
  Filter,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { PersonaClient, PersonaDetail } from '@/services/api';

// Convert slug like "skoutorganic" → "Skoutorganic", "fringesport" → "Fringesport"
function formatClientName(client: PersonaClient): string {
  if (client.client_name) return client.client_name;
  const slug = client.slug ?? client.client_uid ?? client.client_id;
  // Convert snake_case or slug to Title Case
  return slug
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function PersonasPage() {
  const {
    clients,
    selectedClientId,
    personas,
    enriched,
    loading,
    error,
    fetchClients,
    selectClient,
    toggleEnriched,
  } = usePersonasStore();

  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [clientSearch, setClientSearch] = useState('');

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Filter personas client-side by search + active status
  const filteredPersonas = useMemo(() => {
    let list = personas;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.slug ?? '').toLowerCase().includes(q) ||
          (p.description ?? '').toLowerCase().includes(q) ||
          (p.summary_headline ?? '').toLowerCase().includes(q),
      );
    }
    if (activeFilter === 'active') list = list.filter((p) => p.is_active !== false);
    if (activeFilter === 'inactive') list = list.filter((p) => p.is_active === false);
    return list;
  }, [personas, search, activeFilter]);

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const q = clientSearch.toLowerCase();
    return clients.filter(
      (c) =>
        formatClientName(c).toLowerCase().includes(q) ||
        (c.client_uid ?? '').toLowerCase().includes(q),
    );
  }, [clients, clientSearch]);

  const selectedClient = clients.find((c) => c.client_id === selectedClientId);

  return (
    <div className="flex gap-0 -mx-6 -mt-6 h-[calc(100vh-4rem)]">
      {/* ── Left sidebar: client list ── */}
      <aside className="w-64 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-hidden">
        <div className="px-4 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Building2 size={14} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Clients</span>
            {clients.length > 0 && (
              <span className="ml-auto text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">
                {clients.length}
              </span>
            )}
          </div>
          {clients.length > 6 && (
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Filter clients…"
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal/40 focus:border-brand-teal/50"
              />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {filteredClients.length === 0 && !loading && (
            <p className="px-4 py-6 text-xs text-slate-400 text-center">No clients found</p>
          )}
          {filteredClients.map((c) => (
            <ClientRow
              key={c.client_id}
              client={c}
              selected={selectedClientId === c.client_id}
              onClick={() => {
                setSearch('');
                selectClient(c.client_id);
              }}
            />
          ))}
          {loading && clients.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={16} className="animate-spin text-slate-300" />
            </div>
          )}
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-50 overflow-hidden">
        {/* Top bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <div className="flex-1">
            {selectedClient ? (
              <div>
                <h1 className="text-lg font-bold text-navy-900">{formatClientName(selectedClient)}</h1>
                <p className="text-xs text-slate-400 font-mono">{selectedClient.client_uid}</p>
              </div>
            ) : (
              <div>
                <h1 className="text-lg font-bold text-navy-900">Personas</h1>
                <p className="text-xs text-slate-500">Select a client to explore personas</p>
              </div>
            )}
          </div>

          {/* Search */}
          {personas.length > 0 && (
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search personas…"
                className="pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-brand-teal/40 focus:border-brand-teal/50 w-52"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          {/* Active filter */}
          {personas.length > 0 && (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              {(['all', 'active', 'inactive'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={clsx(
                    'px-2.5 py-1 rounded-md text-xs font-medium transition-all capitalize',
                    activeFilter === f
                      ? 'bg-white text-navy-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700',
                  )}
                >
                  {f}
                </button>
              ))}
            </div>
          )}

          {/* Enriched toggle */}
          {selectedClientId && (
            <button
              onClick={toggleEnriched}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border',
                enriched
                  ? 'bg-brand-teal/10 border-brand-teal text-brand-teal-dark'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
              )}
            >
              {enriched ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
              Enriched
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* No client selected */}
        {!selectedClientId && !loading && !error && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Users size={40} className="mx-auto text-slate-200 mb-4" />
              <p className="text-sm font-medium text-slate-500">Select a client from the left</p>
              <p className="text-xs text-slate-400 mt-1">to explore their buyer personas</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={24} className="animate-spin text-slate-400" />
            <span className="ml-3 text-sm text-slate-500">Loading personas…</span>
          </div>
        )}

        {/* Personas grid + detail */}
        {!loading && selectedClientId && (
          <div className="flex flex-1 gap-0 min-h-0 overflow-hidden">
            {/* Persona list */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Result meta */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  {filteredPersonas.length} persona{filteredPersonas.length !== 1 ? 's' : ''}
                  {search && ` matching "${search}"`}
                </span>
                {enriched && (
                  <span className="text-[10px] font-mono bg-brand-teal/10 text-brand-teal px-2 py-0.5 rounded-full border border-brand-teal/20">
                    enriched
                  </span>
                )}
                {activeFilter !== 'all' && (
                  <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Filter size={8} />
                    {activeFilter}
                  </span>
                )}
              </div>

              {filteredPersonas.length === 0 && !loading && (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <User size={28} className="mx-auto text-slate-200 mb-3" />
                  <p className="text-sm text-slate-500">
                    {search ? `No personas match "${search}"` : 'No personas found for this client.'}
                  </p>
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="mt-2 text-xs text-brand-teal hover:underline"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}

              <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                {filteredPersonas.map((p) => (
                  <PersonaCard
                    key={p.slug ?? p.id}
                    persona={p}
                    onClick={() => {
                      const clientUid =
                        clients.find((c) => c.client_id === selectedClientId)?.client_uid ??
                        selectedClientId ??
                        '';
                      const slug = p.slug ?? p.id ?? '';
                      navigate(`/personas/${clientUid}/${slug}`);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client Row (sidebar)
// ---------------------------------------------------------------------------

function ClientRow({
  client,
  selected,
  onClick,
}: {
  client: PersonaClient;
  selected: boolean;
  onClick: () => void;
}) {
  const name = formatClientName(client);
  const sub = client.client_uid && client.client_uid !== name.toLowerCase().replace(/\s+/g, '-')
    ? client.client_uid
    : null;

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-4 py-2.5 flex items-center gap-3 transition-all group',
        selected
          ? 'bg-brand-teal/8 border-r-2 border-brand-teal'
          : 'hover:bg-slate-50 border-r-2 border-transparent',
      )}
    >
      <div
        className={clsx(
          'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold uppercase',
          selected
            ? 'bg-brand-teal/20 text-brand-teal-dark'
            : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200',
        )}
      >
        {name.slice(0, 2)}
      </div>
      <div className="min-w-0 flex-1">
        <p className={clsx('text-xs font-semibold truncate', selected ? 'text-brand-teal-dark' : 'text-navy-900')}>
          {name}
        </p>
        {sub && <p className="text-[10px] text-slate-400 font-mono truncate">{sub}</p>}
      </div>
      {selected && <ChevronRight size={12} className="text-brand-teal flex-shrink-0" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Persona Card
// ---------------------------------------------------------------------------

function PersonaCard({
  persona,
  onClick,
}: {
  persona: PersonaDetail;
  onClick: () => void;
}) {
  const isInactive = persona.is_active === false;

  return (
    <div
      className={clsx(
        'bg-white rounded-xl border shadow-sm p-4 cursor-pointer transition-all group',
        'border-slate-200 hover:border-brand-teal/40 hover:shadow-soft',
        isInactive && 'opacity-60',
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal/20 to-brand-purple/20 flex items-center justify-center text-brand-teal flex-shrink-0">
          <User size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-navy-900 truncate group-hover:text-brand-teal-dark transition-colors">
              {persona.name}
            </h3>
            {isInactive && (
              <span className="text-[9px] bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full flex-shrink-0">
                inactive
              </span>
            )}
          </div>
          <p className="text-[10px] font-mono text-slate-400 truncate">{persona.slug ?? persona.id}</p>
          {(persona.description ?? persona.summary_headline) && (
            <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">
              {persona.description ?? persona.summary_headline}
            </p>
          )}
        </div>
        <ChevronRight size={13} className="text-slate-300 flex-shrink-0 mt-0.5 group-hover:text-brand-teal transition-colors" />
      </div>

      {/* KPIs / decision factors as chips */}
      {(persona.kpis?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {persona.kpis!.slice(0, 3).map((kpi) => (
            <span key={kpi} className="text-[9px] bg-brand-teal/8 text-brand-teal-dark px-1.5 py-0.5 rounded-full border border-brand-teal/20">
              {kpi}
            </span>
          ))}
          {persona.kpis!.length > 3 && (
            <span className="text-[9px] text-slate-400">+{persona.kpis!.length - 3}</span>
          )}
        </div>
      )}
      {(persona.decision_factors?.length ?? 0) > 0 && (persona.kpis?.length ?? 0) === 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {persona.decision_factors!.slice(0, 3).map((df) => (
            <span key={df.name} className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full border border-slate-200">
              {df.display}
            </span>
          ))}
          {persona.decision_factors!.length > 3 && (
            <span className="text-[9px] text-slate-400">+{persona.decision_factors!.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}

